import React from 'react';

import { FacetAccordion } from 'components/search/FacetAccordion/FacetAccordion';
import { useFacetSelection } from 'components/search/Facets/useFacetSelection';
import useMartyContext from 'hooks/useMartyContext';
import type { AutoCompleteDataType, FacetDataType } from 'types/search';

interface FacetsSidebarProps {
  facets: FacetDataType[];
  autoComplete: AutoCompleteDataType;
  hasAutoComplete?: boolean;
  onFacetSelect: (facetGroup: string, facetName: string, selectedFacetGroupIndex: number, selectedFacetIndex: number) => void;
  getFacetUrlPath: (url?: string) => void;
  getFacetUrlParams: (url?: string) => void;
}

export const FacetsSidebar = ({ facets, autoComplete, hasAutoComplete, onFacetSelect, getFacetUrlPath, getFacetUrlParams }: FacetsSidebarProps) => {
  const { testId } = useMartyContext();

  const { handleFacetSelect, handleAccordionChange, debouncedSearchEvent } = useFacetSelection({
    autoComplete,
    hasAutoComplete,
    onFacetSelect,
    getFacetUrlPath,
    getFacetUrlParams,
    facets
  });

  const expandedValues = facets.filter(facet => facet.isExpanded).map(facet => facet.facetField);

  return (
    <FacetAccordion.Root type="multiple" value={expandedValues} onValueChange={handleAccordionChange} className="max-md:px-2">
      {facets.map((facet, facetIndex) => {
        const { facetField, facetFieldDisplayName, values } = facet;

        return (
          <FacetAccordion.Item key={facetField} value={facetField} data-test-id={testId('facetGroup')} facet={facet} context="desktop">
            <FacetAccordion.Trigger id={facetField} data-test-id-facet-head-name={facetFieldDisplayName}>
              <FacetAccordion.DisplayName data-test-id={testId('facetGroupHeadTestId')} />
              <FacetAccordion.SelectedValueNames data-test-id={testId('facetSelectedValues')} />
            </FacetAccordion.Trigger>

            <FacetAccordion.Content>
              {hasAutoComplete && <FacetAccordion.AutoComplete onChange={e => debouncedSearchEvent(facet, e.target.value)} />}

              <FacetAccordion.ValueList data-test-id={testId(`${facetFieldDisplayName}-values`)} autoComplete={autoComplete}>
                {({ facetValue }) => (
                  <FacetAccordion.Value
                    data-test-id={testId(`facet-${facetValue.name}`)}
                    facetValue={facetValue}
                    onFacetSelect={() => {
                      const valueIndex = values.findIndex(value => value.name === facetValue.name);
                      const facetUrl = facetValue?.facetUrl || facetValue?.facetZsoUrl;

                      handleFacetSelect({
                        facetGroup: facetField,
                        facetName: facetValue.name,
                        selectedFacetGroupIndex: facetIndex,
                        selectedFacetIndex: valueIndex,
                        isSelected: facetValue?.selected || false,
                        facetZsoUrl: facetUrl
                      });
                    }}
                  />
                )}
              </FacetAccordion.ValueList>
            </FacetAccordion.Content>
          </FacetAccordion.Item>
        );
      })}
    </FacetAccordion.Root>
  );
};
